// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component } from '@angular/core';
import {Observable, of, Subject, throwError} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HeaderService } from '../../../header.service';
import {catchError, map, switchMap, withLatestFrom} from 'rxjs/operators';
import { LegendPosition, ScaleType } from '@swimlane/ngx-charts';
import {DashboardService, IBitLockerCompliance} from '../../../../services/dashboard.service';
import { TranslateService } from '@ngx-translate/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {AppState} from '../../../store/app.state';
import {selectUser} from '../../../store/user/user.selectors';
import {OrgType} from '../../../../types/group';
import { IManageUser } from '../../../../types/user';

interface ChartData {
  name: string;
  value: number;
}

interface EndpointsAttentionResult {
  response: IBitLockerCompliance;
  chartData: ChartData[];
}

@UntilDestroy({checkProperties: true})
@Component({
    selector: 'app-endpoints-attention',
    templateUrl: './endpoints-attention.component.html',
    styleUrls: ['./endpoints-attention.component.scss', '../dynamic-dashboard.common.scss'],
    standalone: false
})
export class EndpointsAttentionComponent {

  endpointsAttentionResult$?: Observable<EndpointsAttentionResult> =
      new Subject<EndpointsAttentionResult>();

  graphStyle = '185px';

  showLegend = true;
  showLabels = false;
  isDoughnut = true;
  legendPosition: LegendPosition = LegendPosition.Below;
  chooseCustomer = false;

  colorScheme = {
    name: 'endpoints-attention',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: [
      '#5AA454',
      '#A10A28',
    ]
  }

  constructor(
      private headerService: HeaderService,
      private dashboardService: DashboardService,
      private readonly store: Store<AppState>,
      private readonly router: Router,
      private translate: TranslateService
  ) {

    this.endpointsAttentionResult$ =
      this.headerService.orgSelectedChanged.pipe(

      withLatestFrom(this.store.select(selectUser())),

      switchMap(([selectedOrgId, user]: [string, IManageUser]) => {

        if (user.org_type.value !== OrgType.CUSTOMER &&
            selectedOrgId.toLowerCase() === 'all') {

          this.chooseCustomer = true;

          const emptyResult: IBitLockerCompliance = {
            total: 0,
            compliant: 0,
            nonCompliant: 0,
            errors: 0,
          };

          return of(emptyResult);

        } else {
          this.chooseCustomer = false;
        }

        return this.dashboardService.getBitLockerCompliance(
            (selectedOrgId.toLowerCase() === 'all')
                ? null
                : selectedOrgId
        );
      }),

      map((complianceResult: IBitLockerCompliance):
          EndpointsAttentionResult => {

        return {
          response: complianceResult,

          chartData: [
            {
              name: this.translate.instant('COMPLIANT'),
              value: complianceResult.compliant
            },
            {
              name: this.translate.instant('NON_COMPLIANT'),
              value: complianceResult.nonCompliant
            },
          ]
        };
      }),

      catchError(error => {
        return throwError(error);
      }),

      untilDestroyed(this)
    );
  }

  hasData(endpointsAttentionResult: EndpointsAttentionResult) {
    return endpointsAttentionResult?.response?.total > 0;
  }

  onClick(event) {
    this.router.navigate(['/endpoints'], {
      queryParams: {
        endpoints_target: 'encryption',
        bitlocker_compliance: event.label,
      }
    });
  }
}